// ---------------------------------------------------------------------------
// DnD Email Editor – web fonts
//
// A web font is not delivered with the email: the message carries the family
// name inline on each element plus a *reference* to a stylesheet that defines
// the family, and the client downloads the font when the message is opened.
// That means a web font only renders where all of this holds:
//
//   - the client keeps <style> / <link>   (Gmail, Yahoo and Outlook.com do not)
//   - its engine supports @font-face      (Outlook 2007-2021 on Windows does not)
//   - it fetches remote content           (blocked images usually blocks fonts)
//
// In practice that means Apple Mail, iOS Mail, Outlook for Mac, Samsung Mail and
// Thunderbird render it and everyone else gets the next font in the stack — so
// every web font here MUST carry a web-safe fallback stack, and a design using
// one has to look right in both.
//
// Outlook is handled two ways. The definition is hidden from it entirely with a
// `[if !mso]` conditional, so Word never sees a declared-but-unavailable family
// (which is what makes it fall back to Times New Roman instead of continuing
// down the stack), and `mso-ascii/hansi-font-family` names the installed font it
// should use instead. Both are ignored by every other client.
//
// Verified by a live send (2026-07-29): Outlook on Windows rendered Arial, not
// Times New Roman. Outlook there will never show a web font — the only way to
// get one is to have it installed on the machine.
//
// The stylesheet URL is the Google Fonts CDN, matching how the app itself loads
// Inter (see index.html). To self-host instead — which avoids a third-party
// seeing recipients' IP and open time on every send — host the woff2 and change
// `url` below to your own absolute URL; nothing else needs to change.
// ---------------------------------------------------------------------------

export interface WebFont {
  /** Family name as it appears in a font-family stack. */
  name: string;
  /** Label shown in the font pickers. */
  label: string;
  /** Full stack persisted on the model and written into inline styles. */
  stack: string;
  /** Stylesheet defining the family, fetched by the client on open. */
  url: string;
}

export const WEB_FONTS: WebFont[] = [
  {
    name: 'Montserrat',
    label: 'Montserrat (web font)',
    stack: "Montserrat, 'Helvetica Neue', Helvetica, Arial, sans-serif",
    url: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap',
  },
];

/**
 * Fonts installed widely enough for Outlook to be pointed at, in preference
 * order for Windows — where Word's engine runs, and where Helvetica is absent
 * and only resolves through font substitution, so Arial is preferred.
 */
const WEB_SAFE = [
  'Arial',
  'Helvetica',
  'Verdana',
  'Tahoma',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Trebuchet MS',
  'Lucida Console',
];

/** Which web fonts a compiled document actually refers to. */
export function webFontsUsedIn(html: string): WebFont[] {
  if (!html) return [];
  return WEB_FONTS.filter((font) => html.includes(font.name));
}

/**
 * `<head>` markup defining the given fonts, or '' when none are used — an email
 * that uses no web font must not pay for a font request.
 *
 * Both a <link> and an @import are emitted because clients differ on which they
 * honour, and the whole block is hidden from Outlook.
 */
export function webFontHeadHtml(fonts: WebFont[]): string {
  if (fonts.length === 0) return '';
  // The ampersand is escaped in the href (an HTML attribute) but NOT in the
  // @import: CSS does not decode HTML entities, so `&amp;` there would request a
  // URL that does not exist. The asymmetry is deliberate.
  const links = fonts
    .map((f) => `  <link rel="stylesheet" href="${f.url.replace(/&/g, '&amp;')}" />`)
    .join('\n');
  const imports = fonts.map((f) => `    @import url('${f.url}');`).join('\n');
  return `  <!--[if !mso]><!-->
${links}
  <style type="text/css">
${imports}
  </style>
  <!--<![endif]-->`;
}

/**
 * Add the font definitions to a preview document.
 *
 * The HTML-block preview is an iframe, which is a separate document and does not
 * inherit the fonts the app loads for itself — so a block using a web font would
 * preview in the fallback while the sent email uses the real font. Returns the
 * input untouched when no web font is referenced.
 */
export function injectWebFontLinks(html: string): string {
  const fonts = webFontsUsedIn(html);
  if (fonts.length === 0 || typeof DOMParser === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.head) return html;
    for (const font of fonts) {
      const link = doc.createElement('link');
      link.rel = 'stylesheet';
      link.href = font.url;
      doc.head.appendChild(link);
    }
    // The doctype has to be re-added by hand: without it the iframe renders in
    // quirks mode, which changes the box model and misrepresents the block.
    return `<!doctype html>${doc.documentElement.outerHTML}`;
  } catch {
    return html;
  }
}

/** The installed font Outlook should use for a stack, or null if not a web font. */
function outlookFallbackFor(stack: string): string | null {
  if (!WEB_FONTS.some((f) => stack.includes(f.name))) return null;
  const families = stack.split(',').map((part) => part.trim().replace(/^['"]|['"]$/g, ''));
  // Walk WEB_SAFE (not the stack) so the most reliable Windows font present in
  // the stack wins, rather than whichever the author happened to list first.
  return WEB_SAFE.find((safe) => families.includes(safe)) ?? 'Arial';
}

/**
 * A `font-family` declaration, plus the Outlook-only hints when the stack leads
 * with a web font Word cannot fetch.
 */
export function fontFamilyCss(stack: string): string {
  const fallback = outlookFallbackFor(stack);
  if (!fallback) return `font-family: ${stack};`;
  return `font-family: ${stack}; mso-ascii-font-family: ${fallback}; mso-hansi-font-family: ${fallback};`;
}
